import mapValues from 'lodash/mapValues';
import { DOMPurifyWindow, sanitizePreviewHtml } from '../editor/utils/preview-sanitizer';

const RICH_TEXT_TYPES = new Set(['custom-text', 'text', 'title', 'subtitle']);

/**
 * Sanitize HTML before injecting it into the GrapesJS canvas DOM.
 * Matches preview policy: no script tags, no inline handlers, no javascript: URLs.
 * Does not mutate stored model/datasource content; use at render time only.
 */
export function sanitizeEditorCanvasHtml(html: string, currentWindow?: Window | null): string {
  const win = (currentWindow ?? window) as unknown as DOMPurifyWindow;
  return sanitizePreviewHtml(html, win);
}

type EditorComponent = Record<string, unknown>;

const isRecord = (value: unknown): value is EditorComponent =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const sanitizeContent = (value: EditorComponent) =>
  typeof value.content === 'string'
    ? { ...value, content: sanitizeEditorCanvasHtml(value.content) }
    : value;

export function sanitizeEditorComponents(schema: unknown): EditorComponent[] {
  const components = Array.isArray(schema)
    ? schema
    : isRecord(schema) && Array.isArray(schema.components)
    ? schema.components
    : [];

  return components.filter(isRecord).map(component => {
    const sanitized =
      typeof component.type === 'string' && RICH_TEXT_TYPES.has(component.type)
        ? {
            ...sanitizeContent(component),
            ...(isRecord(component.i18nAttributes) && {
              i18nAttributes: mapValues(component.i18nAttributes, value =>
                isRecord(value) ? sanitizeContent(value) : value,
              ),
            }),
          }
        : component;

    return Array.isArray(component.components)
      ? { ...sanitized, components: sanitizeEditorComponents(component.components) }
      : sanitized;
  });
}
